import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useChecklistTemplate, useCreateChecklistRun } from '@/lib/queries';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { currentYear } from '@houseops/shared';
import { ListChecks } from 'lucide-react';

export default function ChecklistTemplateDetail() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const q = useChecklistTemplate(idOrSlug);
  const create = useCreateChecklistRun();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  const { template, items } = q.data;

  async function startRun() {
    setError(null);
    try {
      const result = await create.mutateAsync({
        templateId: template.id,
        year: currentYear(),
        season: template.season ?? undefined,
      });
      navigate(`/checklists/runs/${result.run.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start run.');
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <Link to="/checklists" className="hover:text-foreground">
            Checklists
          </Link>{' '}
          <span aria-hidden>·</span> Template <span aria-hidden>·</span> {template.slug}
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          {template.title}
        </h1>
        {template.description ? (
          <p className="text-muted-foreground max-w-2xl">{template.description}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
          {template.season && template.season !== 'any' ? (
            <Badge variant="secondary" className="font-mono text-[10px] tracking-wider">
              {template.season}
            </Badge>
          ) : null}
          {template.recurrenceRule ? (
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {template.recurrenceRule}
            </span>
          ) : null}
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground font-tabular">
            {items.length} items
          </span>
        </div>
      </header>

      <Card className="border-primary/20 bg-primary/5">
        <CardHeader className="space-y-1">
          <CardTitle className="font-display text-base">Start a run</CardTitle>
          <CardDescription>
            Creates a new checklist run for {template.season && template.season !== 'any' ? `${capitalize(template.season)} ${currentYear()}` : `${currentYear()}`} with all {items.length} items
            ready to tick off.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <Button
            type="button"
            onClick={startRun}
            disabled={create.isPending}
            className="w-full sm:w-auto h-11 text-base"
            data-testid="start-run"
          >
            <ListChecks className="h-4 w-4 mr-2" aria-hidden />
            {create.isPending ? 'Starting…' : 'Start run'}
          </Button>
          {error ? <p className="text-sm text-destructive mt-3">{error}</p> : null}
        </CardContent>
      </Card>

      <section className="space-y-2">
        <h2 className="font-display text-xl font-semibold tracking-tight">Items</h2>
        <ol
          className="divide-y divide-rule rounded-md border border-rule bg-card"
          data-testid="template-items"
        >
          {items.map((it, ix) => (
            <li
              key={it.id}
              className="flex items-start gap-3 px-4 py-3"
            >
              <span className="font-mono text-[11px] tabular-nums uppercase tracking-wider text-muted-foreground pt-0.5 w-6 shrink-0">
                {String(ix + 1).padStart(2, '0')}
              </span>
              <span className="flex-1 text-foreground/90">{it.label}</span>
              {it.required ? (
                <Badge variant="outline" className="font-mono text-[10px] tracking-wider shrink-0">
                  required
                </Badge>
              ) : null}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
